import { Component } from '@theme/component';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages accordion open/close behavior.
 *
 * @typedef {Object} State
 * @property {HTMLDetailsElement | null} activeItem - The currently active accordion.
 *
 * @extends {Component}
 */
class AccordionsController extends Component {
  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Open the closest accordion
   * @param {Event} [event]
   */
  open(event) {
    event?.preventDefault();
    const details = this.#getDetailsElement(event);
    return this.#open(details);
  }

  /**
   * Open the specified accordion
   * @param {HTMLDetailsElement|null} [details]
   */
  #open(details) {
    if (!details || this.#state.activeItem === details) return;

    const summary = details.querySelector('summary');

    if (!summary) return;

    if (this.#state.activeItem) {
      this.#close(this.#state.activeItem);
    }

    details.setAttribute('open', '');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    this.#state.activeItem = details;
  }

  /**
   * Close the closest accordion or the specified accordion
   *
   * @param {HTMLDetailsElement|null} [details]
   */
  #close(details) {
    if (!details) return;

    const summary = details.querySelector('summary');

    if (!summary) return;

    details.classList.remove('menu-open');
    this.#state.activeItem = null;

    onAnimationEnd(details, () => {
      details.removeAttribute('open');
    });
  }

  /**
   * Close the active accordion
   * @param {Event} [event]
   */
  back(event) {
    event?.preventDefault();
    return this.#close(this.#state.activeItem);
  }

  /**
   * Handle right arrow key navigation to open accordion and focus first item
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   */
  handleRightArrowNavigation(event, currentTarget) {
    if (currentTarget.tagName !== 'SUMMARY') return;

    event.preventDefault();
    const details = currentTarget.closest('details');
    if (details) {
      this.#open(details);
            // Focus first navigable item in the submenu after animation completes
      onAnimationEnd(details, () => {
        // Add delay to ensure menu transition is fully complete
        setTimeout(() => {
          // Only attempt to focus if the details element is still in the DOM and has submenu content
          if (!details || !details.hasAttribute('open')) return;

          const firstSubItem = details.querySelector('.menu-drawer__submenu summary, .menu-drawer__submenu a.menu-drawer__menu-item, .menu-drawer__menu--childlist summary, .menu-drawer__menu--childlist a.menu-drawer__menu-item');
          if (firstSubItem && firstSubItem instanceof HTMLElement && firstSubItem.offsetParent !== null) {
            try {
              firstSubItem.focus();
            } catch (error) {
              // Silently handle focus errors that might occur during menu transitions
              console.debug('Focus failed during menu transition:', error);
            }
          }
        }, 200);
      });
    }
  }

  /**
   * Handle Enter key navigation to open accordion and focus first item
   * @param {KeyboardEvent} event
   * @param {Element} currentTarget
   */
  handleEnterKeyNavigation(event, currentTarget) {
    // Reuse the same logic as right arrow navigation for consistency
    this.handleRightArrowNavigation(event, currentTarget);
  }

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();
    const firstAccordion = this.#findFirstAccordion();

    if (!firstAccordion) return;

    const isOpen =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    if (isOpen) {
      this.#open(firstAccordion);
    } else if (this.#state.activeItem) {
      this.#close(this.#state.activeItem);
    }
  }

  #findFirstAccordion() {
    return this.querySelector('details');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement | null}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return null;

    return event.target.closest('details') ?? null;
  }
}

if (!customElements.get('accordions-controller')) {
  customElements.define('accordions-controller', AccordionsController);
}
